/**
 * Color helpers shared by tag / category chips.
 *
 * We store colors as `#rrggbb` strings (what `<input type="color">`
 * emits and what Carbon Fields persists for the public site). Empty
 * string means "no colour set — use default styling".
 */

const HEX = /^#([0-9a-f]{6})$/i

/** Quick validity check for the hex strings we round-trip with WP. */
export function isHexColor(value: string): boolean {
  return HEX.test(value.trim())
}

/**
 * Relative luminance per WCAG 2.x. Output is in [0, 1].
 * Returns null if the input isn't a parseable `#rrggbb`.
 */
export function luminance(hex: string): number | null {
  const m = HEX.exec(hex.trim())
  if (!m || !m[1]) return null
  const v = m[1]
  const r = parseInt(v.slice(0, 2), 16) / 255
  const g = parseInt(v.slice(2, 4), 16) / 255
  const b = parseInt(v.slice(4, 6), 16) / 255
  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

/**
 * Pick a readable text colour for a coloured background. Threshold
 * 0.5 lands closer to W3C's "contrast >= 4.5" recommendation than the
 * naive `lum > 0.5 → black` rule (which fails on yellows / cyans).
 *
 * Falls back to `#000000` for non-hex input so the chip never blanks.
 */
export function readableTextColor(bgHex: string): '#000000' | '#ffffff' {
  const l = luminance(bgHex)
  if (l === null) return '#000000'
  return l > 0.5 ? '#000000' : '#ffffff'
}
